/**
 * PMS types and enums for the Performance Management System
 * Requirements: 1.1, 2.1, 3.2, 6.1, 10.1, 11.2, 12.1, 14.1, 23.1
 */

/**
 * PMS Role enumeration — module-level roles for PMS access control
 * Requirements: 1.1
 */
export enum PMSRole {
  EMPLOYEE = 'Employee',
  MANAGER = 'Manager',
  HR_ADMIN = 'HR_Admin',
  SUPER_ADMIN = 'Super_Admin'
}

/**
 * Objective status workflow states
 * Requirements: 2.1, 2.2, 2.3
 */
export enum ObjectiveStatus {
  DRAFT = 'Draft',
  SUBMITTED = 'Submitted',
  APPROVED = 'Approved',
  RETURNED = 'Returned',
  IN_PROGRESS = 'In_Progress',
  UNDER_REVIEW = 'Under_Review',
  COMPLETED = 'Completed'
}

/**
 * Review cycle status
 * Requirements: 14.1
 */
export enum ReviewCycleStatus {
  DRAFT = 'Draft',
  ACTIVE = 'Active',
  LOCKED = 'Locked',
  COMPLETED = 'Completed'
}

/**
 * Review phase — HR-controlled gate on a child (Mid-Year / Year-End) review cycle.
 * Deliberately separate from ReviewCycleStatus: status describes the annual-cycle
 * lifecycle (objective creation/lock), reviewPhase describes the review lifecycle on
 * a child cycle. Mixing them would mean the enum held values only valid depending on
 * which kind of cycle row you're looking at.
 */
export enum ReviewPhase {
  NOT_STARTED = 'NOT_STARTED',
  OPEN = 'OPEN',
  CLOSED = 'CLOSED'
}

/**
 * Review cycle type — determines review behavior
 * Requirements: 14.2, 14.3
 */
export enum ReviewCycleType {
  YEAR_END = 'YEAR_END',
  HIGH_LEVEL = 'HIGH_LEVEL',
}

/**
 * Review cycle type display labels
 */
export const REVIEW_CYCLE_TYPE_LABELS: Record<ReviewCycleType, string> = {
  [ReviewCycleType.YEAR_END]: 'Year End Review',
  [ReviewCycleType.HIGH_LEVEL]: 'High Level Review',
};

/**
 * How an objective's progress is tracked. Plain string on the wire (not a GraphQL
 * enum) so adding a future option (e.g. Milestone) is a frontend-only change.
 */
export enum TrackBy {
  PERCENT = 'PERCENT',
  RATING = 'RATING'
}

export const TRACK_BY_LABELS: Record<TrackBy, string> = {
  [TrackBy.PERCENT]: 'Percent',
  [TrackBy.RATING]: 'Rating',
};

/**
 * Review type enumeration
 * Requirements: 8.1, 9.1
 */
export enum ReviewType {
  MID_YEAR = 'MID_YEAR',
  END_YEAR = 'END_YEAR',
  QUARTERLY = 'QUARTERLY'
}

/**
 * Rating scale — 5-point evaluation
 * Requirements: 10.1
 */
export enum RatingScale {
  OUTSTANDING = 5,
  EXCEEDS_EXPECTATIONS = 4,
  MEETS_EXPECTATIONS = 3,
  NEEDS_IMPROVEMENT = 2,
  UNSATISFACTORY = 1
}

export enum ObjectiveEvaluationSummary {
  GOAL_ACHIEVED = 'Goal Achieved',
  PARTIALLY_ACHIEVED = 'Partially Achieved',
  NOT_ACHIEVED = 'Not Achieved'
}

/**
 * Objective closure status for year-end reviews
 */
export enum ObjectiveClosureStatus {
  COMPLETED = 'Completed',
  PARTIALLY_COMPLETED = 'Partially Completed',
  NOT_COMPLETED = 'Not Completed'
}

/**
 * Review workflow status for manager/HR tracking
 */
export enum ReviewWorkflowStatus {
  DRAFT = 'Draft',
  IN_PROGRESS = 'In_Progress',
  SUBMITTED = 'Submitted',
  COMPLETED = 'Completed',
  REOPEN_REQUESTED = 'Reopen_Requested'
}

/**
 * Review audit history action types
 */
export enum ReviewHistoryAction {
  REVIEW_CREATED = 'Review Created',
  DRAFT_SAVED = 'Draft Saved',
  OBJECTIVE_REVIEWED = 'Objective Reviewed',
  REVIEW_SUBMITTED = 'Review Submitted',
  REVIEW_PUBLISHED = 'Review Published',
  REVIEW_REOPENED = 'Review Reopened',
  /** Manager sent the self-review back to the employee for revision. */
  REVIEW_RETURNED = 'Review Returned',
  /** Employee submitted again after a return. */
  REVIEW_RESUBMITTED = 'Review Resubmitted',
  REOPEN_REQUESTED = 'Reopen Requested',
  REVIEW_LOCKED = 'Review Locked',
  REVIEW_UNLOCKED = 'Review Unlocked',
  REVIEW_ACKNOWLEDGED = 'Review Acknowledged'
}

/**
 * Chat thread type
 * Requirements: 11.2
 */
export enum ChatThreadType {
  OBJECTIVE = 'OBJECTIVE',
  MILESTONE = 'MILESTONE'
}

/**
 * Attachment parent type
 * Requirements: 12.1
 */
export enum AttachmentParentType {
  OBJECTIVE = 'OBJECTIVE',
  MILESTONE = 'MILESTONE'
}

/**
 * Allowed attachment file types
 * Requirements: 12.4
 */
export const ALLOWED_FILE_TYPES = ['pdf', 'png', 'jpg', 'jpeg', 'doc', 'docx', 'xls', 'xlsx', 'csv'];

/**
 * Maximum file size in MB
 * Requirements: 12.3
 */
export const MAX_FILE_SIZE_MB = 10;

// --- Core Interfaces ---

/**
 * Review cycle configuration
 * Used for both Annual Objective Cycles (parent) and Review Cycles (child).
 * Annual cycles have annualCycleId = undefined/null.
 * Review cycles have annualCycleId pointing to their parent annual cycle.
 * Requirements: 14.1
 */
export interface ReviewCycle {
  id: string;
  name: string;
  reviewCycleType?: ReviewCycleType;
  startDate: string;
  endDate: string;
  midYearDeadline: string;
  endYearDeadline: string;
  submissionDeadline: string;
  status: ReviewCycleStatus;
  isLocked: boolean;
  annualCycleId?: string;
  /** Only ever set on child (Mid-Year/Year-End) cycles — see ReviewPhase doc comment. */
  reviewPhase?: ReviewPhase;
  createdAt: string;
  updatedAt: string;
}

/**
 * Type alias for clarity — Annual Objective Cycles are ReviewCycle records
 * where annualCycleId is undefined (they ARE the parent).
 */
export type AnnualCycle = ReviewCycle;

/**
 * Performance objective
 * Requirements: 2.1, 23.1
 */
export interface Objective {
  id: string;
  employeeId: string;
  employeeName: string;
  reviewCycleId: string;
  title: string;
  description: string;
  measureOfSuccess: string;
  startDate: string;
  endDate: string;
  weightage: number;
  progressPercentage: number;
  finalProgressPercentage?: number;
  managerFinalComment?: string;
  evaluationSummary?: ObjectiveEvaluationSummary;
  status: ObjectiveStatus;
  employeeComments: string;
  managerComments: string;
  managerId: string;
  managerName: string;
  isLocked: boolean;
  trackBy?: TrackBy;
  createdAt: string;
  updatedAt: string;
}

/**
 * Milestone within an objective
 * Requirements: 3.2
 */
export interface Milestone {
  id: string;
  objectiveId: string;
  title: string;
  description: string;
  dueDate: string;
  weightage: number;
  progressPercentage: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Immutable version record for objective changes
 * Requirements: 6.1
 */
export interface ObjectiveVersion {
  id: string;
  objectiveId: string;
  action?: string;
  changedFields: string[];
  previousValues: Record<string, unknown>;
  newValues: Record<string, unknown>;
  changedBy: string;
  changedByName: string;
  timestamp: string;
  statusAtChange: ObjectiveStatus;
}

/**
 * Chat message in an objective or milestone thread
 * Requirements: 11.2
 */
export interface ChatMessage {
  id: string;
  threadType: ChatThreadType;
  threadId: string;
  senderId: string;
  senderName: string;
  content: string;
  timestamp: string;
}

/**
 * Per-objective rating within a review
 * Requirements: 9.2, 10.1
 */
export interface ObjectiveRating {
  objectiveId: string;
  objectiveTitle: string;
  /**
   * Optional because a Percent-tracked objective has no objective-level rating to give.
   *
   * It used to be required, which is why the self-review form fell back to the employee's
   * *overall* rating for every objective — stamping a rating the employee never chose. An
   * absent rating now means exactly that: none was selected.
   */
  rating?: RatingScale;
  /**
   * The employee's own self-assessed progress, written only by the self review.
   *
   * Separate from finalProgressPercentage because the two were previously the same field: the
   * employee submitted their figure into it and the manager's Final Progress then overwrote it,
   * destroying the self-assessment with nowhere else to recover it from. The manager must never
   * write this one.
   *
   * Optional for backward compatibility — records written before this field existed carry only
   * finalProgressPercentage. Read via employeeProgressOf(), which falls back for those.
   */
  employeeProgressPercentage?: number;
  /** The manager's final progress figure. Written only by the manager's evaluation. */
  finalProgressPercentage: number;
  comments: string;
  managerFinalComment?: string;
  evaluationSummary?: ObjectiveEvaluationSummary;
  closureStatus?: ObjectiveClosureStatus;
  isReviewed?: boolean;
}

/**
 * Immutable review audit history entry
 */
export interface ReviewHistoryEntry {
  id: string;
  action: ReviewHistoryAction | string;
  performedBy: string;
  performedByName: string;
  timestamp: string;
  previousValue?: string;
  newValue?: string;
}

/**
 * Manager reopen request awaiting HR approval
 */
export interface ReviewReopenRequest {
  reason: string;
  requestedBy: string;
  requestedByName: string;
  requestedAt: string;
  status: 'Pending' | 'Approved' | 'Rejected';
  resolvedBy?: string;
  resolvedByName?: string;
  resolvedAt?: string;
}

/**
 * Extended review metadata stored as AWSJSON
 */
export interface ReviewMetadata {
  name?: string;
  reviewName?: string;
  workflowStatus?: ReviewWorkflowStatus;
  reviewCycleName?: string;
  submittedAt?: string;
  publishedAt?: string;
  reviewHistory?: ReviewHistoryEntry[];
  reopenRequest?: ReviewReopenRequest;
  reopenedBy?: string;
  reopenedByName?: string;
  reopenedAt?: string;
  reopenReason?: string;
  /*
   * Manager "Return to Employee". Modelled here rather than as new columns because
   * reviewMetadata is already AWSJSON and already carries the parallel reopen* fields — so the
   * whole feature needs no GraphQL schema change and no Terraform apply.
   *
   * returnedAt is compared against employeeSubmittedAt to decide whose turn it is, which is
   * what lets a review be returned and resubmitted any number of times without extra state.
   */
  returnedAt?: string;
  returnReason?: string;
  returnedBy?: string;
  returnedByName?: string;
  /*
   * Free-form conversation between the employee and their manager about this review.
   *
   * Deliberately NOT stored in overallComments / managerFinalComment / ObjectiveRating.comments.
   * Those are the manager's formal evaluation fields, and the review-isolation work already had
   * to unpick one case where an employee's note surfaced under a manager heading — see
   * pms-new-review-isolation.test.ts. A discussion message is a different kind of thing from a
   * verdict and gets its own home so the two can never be confused.
   *
   * Modelled in reviewMetadata for the same reason as returnedAt above: it is already AWSJSON,
   * so this needs no GraphQL schema change, no resolver change and no Terraform apply. Scoped
   * per review record, which is what keeps each review cycle's conversation independent.
   */
  discussion?: ReviewDiscussionMessage[];
}

/**
 * One message in a review's discussion thread.
 *
 * authorRole is stored rather than re-derived on read: it records who the person was *when they
 * wrote it*, which is what the thread should show even if their role changes later.
 */
export interface ReviewDiscussionMessage {
  id: string;
  authorId: string;
  authorName: string;
  authorRole: 'employee' | 'manager';
  text: string;
  timestamp: string;
}

/**
 * Performance review record
 * Requirements: 8.1, 9.1
 */
export interface Review {
  id: string;
  name?: string;
  reviewName?: string;
  employeeId: string;
  employeeName: string;
  reviewCycleId: string;
  reviewCycleName?: string;
  reviewType: ReviewType;
  quarter?: number;
  objectiveRatings: ObjectiveRating[];
  overallRating?: RatingScale;
  suggestedRating?: number;
  overallComments: string;
  developmentSuggestions: string;
  reviewerId: string;
  reviewerName: string;
  timestamp: string;
  createdAt: string;
  updatedAt: string;
  workflowStatus?: ReviewWorkflowStatus;
  isAcknowledged?: boolean;
  acknowledgedAt?: string;
  acknowledgedBy?: string;
  reviewHistory?: ReviewHistoryEntry[];
  reviewMetadata?: ReviewMetadata;
  reopenRequest?: ReviewReopenRequest;
  submittedAt?: string;
  publishedAt?: string;
  /** Employee's own self-assessment — filled in before the manager can review. */
  employeeRating?: RatingScale;
  employeeRatingDescription?: string;
  employeeComment?: string;
  employeeSubmittedAt?: string;
  /** Set once the manager submits their side; distinct from `timestamp`/`updatedAt`. */
  managerSubmittedAt?: string;
}

/**
 * File attachment metadata
 * Requirements: 12.1
 */
export interface Attachment {
  id: string;
  parentType: AttachmentParentType;
  parentId: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  s3Key: string;
  uploadedBy: string;
  uploadedAt: string;
}

/**
 * Audit log entry for admin actions
 */
export interface AuditLogEntry {
  id: string;
  action: string;
  performedBy: string;
  performedByName: string;
  details: string;
  timestamp: string;
}

// --- Input Interfaces ---

/**
 * Input for creating an objective
 * Requirements: 2.1
 */
export interface CreateObjectiveInput {
  reviewCycleId: string;
  title: string;
  description: string;
  measureOfSuccess: string;
  startDate: string;
  endDate: string;
  weightage: number;
  employeeComments?: string;
  trackBy?: TrackBy;
  /**
   * The objective's OWN progress — not derived from, and not the same thing as, its milestones'
   * progress. Optional: omitted on create means "start at 0", which is what the resolver
   * defaults to, so existing callers are unaffected.
   */
  progressPercentage?: number;
}

/**
 * Input for updating an objective
 */
export interface UpdateObjectiveInput {
  title?: string;
  description?: string;
  measureOfSuccess?: string;
  startDate?: string;
  endDate?: string;
  weightage?: number;
  progressPercentage?: number;
  finalProgressPercentage?: number;
  managerFinalComment?: string;
  evaluationSummary?: ObjectiveEvaluationSummary;
  employeeComments?: string;
  managerComments?: string;
  status?: ObjectiveStatus;
  trackBy?: TrackBy;
}

/**
 * Input for creating a milestone
 * Requirements: 3.2
 */
export interface CreateMilestoneInput {
  objectiveId: string;
  title: string;
  description: string;
  dueDate: string;
  weightage?: number;
  progressPercentage?: number;
}

/**
 * Input for creating a review cycle (or annual cycle).
 * For annual cycles: annualCycleId is omitted, deadline fields are optional.
 * For review cycles: annualCycleId is required, deadline fields are used.
 * Requirements: 14.1
 */
export interface CreateReviewCycleInput {
  name: string;
  reviewCycleType?: ReviewCycleType;
  startDate: string;
  endDate: string;
  midYearDeadline?: string;
  endYearDeadline?: string;
  submissionDeadline?: string;
  annualCycleId?: string;
}

/**
 * Input for creating a review
 * Requirements: 9.1
 */
export interface CreateReviewInput {
  name?: string;
  reviewName?: string;
  employeeId: string;
  reviewCycleId: string;
  reviewCycleName?: string;
  reviewType: ReviewType;
  quarter?: number;
  objectiveRatings: ObjectiveRating[];
  overallRating: RatingScale;
  overallComments: string;
  developmentSuggestions: string;
  workflowStatus?: ReviewWorkflowStatus;
  reviewMetadata?: ReviewMetadata;
}

/**
 * Input for updating an existing review
 */
export interface UpdateReviewInput {
  objectiveRatings?: ObjectiveRating[];
  overallRating?: RatingScale;
  suggestedRating?: number;
  overallComments?: string;
  developmentSuggestions?: string;
  workflowStatus?: ReviewWorkflowStatus;
  isAcknowledged?: boolean;
  acknowledgedAt?: string;
  acknowledgedBy?: string;
  reviewMetadata?: ReviewMetadata;
  employeeRating?: RatingScale;
  employeeRatingDescription?: string;
  employeeComment?: string;
  employeeSubmittedAt?: string;
  managerSubmittedAt?: string;
  reviewerId?: string;
  reviewerName?: string;
}

/**
 * Input for the employee's own self-review submission. Deliberately separate from
 * CreateReviewInput/UpdateReviewInput, which assume a manager-authored review
 * (overallRating required, reviewerId supplied by the caller) — this one doesn't.
 */
export interface SubmitSelfReviewInput {
  employeeId: string;
  employeeName: string;
  reviewCycleId: string;
  reviewType: ReviewType;
  objectiveRatings: ObjectiveRating[];
  employeeRating: RatingScale;
  employeeRatingDescription: string;
  employeeComment: string;
  /**
   * Who will review this. Optional only because it is unknowable when the employee has no
   * objectives yet; when it is known it must be sent, or the review is created with no
   * reviewer and every screen that reads reviewerName falls back to "Not assigned".
   */
  reviewerId?: string;
  reviewerName?: string;
}

/**
 * Input for creating a chat message
 * Requirements: 11.2
 */
export interface CreateChatMessageInput {
  threadType: ChatThreadType;
  threadId: string;
  content: string;
}

/**
 * Input for creating an attachment
 * Requirements: 12.1
 */
export interface CreateAttachmentInput {
  parentType: AttachmentParentType;
  parentId: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  s3Key: string;
}
